import { useState } from 'react';
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  TextField,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import { useFileBrowserStore } from '@/hooks';

export function CreateFolderDialog() {
  const { isCreateFolderOpen, isCreatingFolder, setCreateFolderOpen, createFolder } =
    useFileBrowserStore();
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    const trimmedName = folderName.trim();

    if (!trimmedName) {
      setError('Folder name is required');
      return;
    }

    // Validate folder name
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
    if (invalidChars.test(trimmedName)) {
      setError('Folder name contains invalid characters');
      return;
    }

    try {
      await createFolder(trimmedName);
      setFolderName('');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    }
  };

  const handleDismiss = () => {
    setFolderName('');
    setError(null);
    setCreateFolderOpen(false);
  };

  return (
    <Dialog
      hidden={!isCreateFolderOpen}
      onDismiss={handleDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Create New Folder',
      }}
      modalProps={{
        isBlocking: isCreatingFolder,
      }}
    >
      <TextField
        label="Folder name"
        value={folderName}
        onChange={(_, value) => {
          setFolderName(value || '');
          setError(null);
        }}
        errorMessage={error || undefined}
        disabled={isCreatingFolder}
        autoFocus
      />

      <DialogFooter className="create-folder-footer">
        {isCreatingFolder ? (
          <Spinner size={SpinnerSize.small} label="Creating" />
        ) : (
          <>
            <PrimaryButton text="Create" onClick={handleCreate} disabled={!folderName.trim()} />
            <DefaultButton text="Cancel" onClick={handleDismiss} />
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}
